/**
 * i18next 초기화 (다국어 4종: ko·en·zh·ja)
 *
 * - fallbackLng: 'ko'
 * - supportedLngs: ['ko','en','zh','ja']
 * - 처음 언어: 로그인 전 화면에서 기억한 언어({@link readBrowserLang}) → 없으면 LanguageDetector(navigator).
 *   🔴 로그인 사용자의 언어 SSOT 는 DB(users.locale)이고 GET /api/auth/me 로 받아 적용함.
 *   브라우저 기억은 로그인 전 화면 표시용이며 로그인하면 계정 언어로 덮어씀(browserLang.ts).
 * - interpolation.escapeValue: false (React가 XSS 이스케이프 담당)
 *
 * 로케일 리소스(JSON)는 콘텐츠 에이전트가 채움. 빈 객체라도 import 가능.
 * UI 표시 문자열만 여기서 관리. 동적 메타 라벨은 resolveLabel() 사용.
 */
import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import en from "./locales/en.json";
import ja from "./locales/ja.json";
import ko from "./locales/ko.json";
import zh from "./locales/zh.json";
import { readBrowserLang } from "./browserLang";

/** 지원 언어 코드 (단일 출처) */
export const SUPPORTED_LNGS = ["ko", "en", "zh", "ja"] as const;
export type SupportedLng = (typeof SUPPORTED_LNGS)[number];

/** 임의 문자열이 지원 언어 코드인지 판별 (서버 저장값 검증용) */
export function isSupportedLng(value: unknown): value is SupportedLng {
  return typeof value === "string" && (SUPPORTED_LNGS as readonly string[]).includes(value);
}

/** 언어 코드 → 원어 표기(언어 선택 목록 — 언어명은 항상 자기 언어) */
export const LANG_NATIVE_LABEL: Record<SupportedLng, string> = {
  ko: "한국어", // i18n-allow: 언어명은 항상 자기 언어 표기
  en: "English",
  ja: "日本語",
  zh: "中文",
};

const browserLang = readBrowserLang();

void i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // 기억한 언어가 있으면 감지기를 건너뜀
    lng: isSupportedLng(browserLang) ? browserLang : undefined,
    fallbackLng: "ko",
    supportedLngs: SUPPORTED_LNGS as unknown as string[],
    resources: {
      ko: { translation: ko },
      en: { translation: en },
      zh: { translation: zh },
      ja: { translation: ja },
    },
    interpolation: {
      escapeValue: false,
    },
    detection: {
      // 기억한 언어가 없을 때의 폴백. 캐시 없음 — 기억은 browserLang.ts, 로그인 후 언어는 /auth/me 의 DB 값.
      order: ["navigator"],
      caches: [],
    },
  });

/** tAll 보간 값 — 언어와 무관하면 객체, 언어마다 다른 값(단계 이름 등 다국어 라벨)이 있으면 언어를 받는 함수. */
export type TAllParams = Record<string, unknown> | ((lng: SupportedLng) => Record<string, unknown>);

/**
 * 한 번역 키를 지원 언어 4개 모두로 번역한 맵 — 받는 사람 언어로 적재되는 알림(POST /notifications 의
 * titleI18n·bodyI18n)용. 화면 표시에는 t() 를 씀.
 */
export function tAll(key: string, params?: TAllParams): Record<SupportedLng, string> {
  return Object.fromEntries(
    SUPPORTED_LNGS.map((lng) => {
      const p = typeof params === "function" ? params(lng) : params;
      return [lng, i18next.t(key, { ...p, lng })];
    }),
  ) as Record<SupportedLng, string>;
}

/**
 * 화면이 만든 시스템 문구 사유(결정 E4) — 봉인(해시) 대상 `reason` 은 ko 문장, `reasonI18n` 은 4개 언어 맵.
 * 서버는 맵을 감사 구조 데이터에 담아 보는 사람 언어로 다시 그린다(`reason` ≠ `reasonI18n.ko` 면 400).
 * 사용자가 직접 쓴 사유에는 쓰지 않는다(입력값은 원문 그대로 `reason` 만).
 */
export function systemReason(
  key: string,
  params?: TAllParams,
): { reason: string; reasonI18n: Record<SupportedLng, string> } {
  const reasonI18n = tAll(key, params);
  return { reason: reasonI18n.ko, reasonI18n };
}

export default i18next;
